import React, { useCallback, useEffect, useMemo, useState } from 'react';
import './DrillDownModal.css';
import { apiService } from '../../services/api';
import { API_ENDPOINTS } from '../../config/apiConfig';
import LoadingSpinner from './LoadingSpinner';
import OemqTrendGraph from './OemqTrendGraph';

const safeNumber = (value) => {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }

  if (typeof value === 'string') {
    const cleaned = value.replace(/[^\d.-]/g, '');
    if (!cleaned) {
      return null;
    }
    const parsed = parseFloat(cleaned);
    return Number.isNaN(parsed) ? null : parsed;
  }

  return null;
};

const formatPercentageValue = (value) => {
  const numeric = safeNumber(value);
  if (numeric === null) {
    return 'N/A';
  }

  const decimals = Math.abs(numeric) >= 100 ? 0 : 1;
  return `${numeric.toFixed(decimals)}%`;
};

const getMetricBarWidth = (value) => {
  const numeric = safeNumber(value);
  if (numeric === null) {
    return '0%';
  }
  const clamped = Math.max(0, Math.min(100, numeric));
  return `${clamped}%`;
};

const getMetricSeverity = (value) => {
  const numeric = safeNumber(value);
  if (numeric === null) {
    return 'metric-na';
  }
  if (numeric >= 20) {
    return 'metric-high';
  }
  if (numeric >= 10) {
    return 'metric-medium';
  }
  if (numeric > 0) {
    return 'metric-low';
  }
  return 'metric-na';
};

const getPercentageSeverity = (value) => {
  const numeric = safeNumber(value);
  if (numeric === null) {
    return 'metric-na';
  }
  if (numeric > 80) {
    return 'metric-high'; // Green
  }
  if (numeric >= 60) {
    return 'metric-medium'; // Yellow
  }
  if (numeric > 0) {
    return 'metric-low'; // Red
  }
  return 'metric-na';
};

const determineCoverageLevel = (value) => {
  const numeric = safeNumber(value);
  if (numeric === null) {
    return 'na';
  }
  if (numeric >= 90) {
    return 'high';
  }
  if (numeric >= 60) {
    return 'medium';
  }
  if (numeric > 0) {
    return 'low';
  }
  return 'na';
};

const determineQualityLevel = (rating, coverageValue, emqScoreValue) => {
  if (rating) {
    const normalized = rating.toString().toLowerCase();
    if (normalized.includes('high')) return 'high';
    if (normalized.includes('medium')) return 'medium';
    if (normalized.includes('low')) return 'low';
  }

  if (typeof emqScoreValue === 'number') {
    if (emqScoreValue >= 8) return 'high';
    if (emqScoreValue >= 6) return 'medium';
    if (emqScoreValue > 0) return 'low';
  }

  const coverageNumeric = safeNumber(coverageValue);
  if (coverageNumeric !== null) {
    if (coverageNumeric >= 90) return 'high';
    if (coverageNumeric >= 60) return 'medium';
    if (coverageNumeric > 0) return 'low';
  }

  return 'na';
};

const formatRatingLabel = (rating, qualityLevel) => {
  if (rating) {
    return rating;
  }

  if (qualityLevel === 'na') {
    return 'N/A';
  }

  return qualityLevel.charAt(0).toUpperCase() + qualityLevel.slice(1);
};

const formatScoreValue = (value) => {
  const numeric = safeNumber(value);
  if (numeric === null) {
    return { formatted: '—', numeric: null };
  }
  return { formatted: numeric.toFixed(1), numeric };
};

const normalizeDiagnosticsList = (diagnostics, baseId, sourceLabel) => {
  if (!Array.isArray(diagnostics) || diagnostics.length === 0) {
    return [];
  }
  return diagnostics.map((diagnostic, index) => ({
    id: `${baseId}-diag-${sourceLabel}-${index}`,
    name: diagnostic?.name || 'Diagnostic',
    description: diagnostic?.description || 'No description provided.',
    solution: diagnostic?.solution || 'No solution provided.',
    percentage: safeNumber(diagnostic?.percentage),
    affectedEventCount: diagnostic?.affectedEventCount ?? diagnostic?.affected_event_count ?? null,
    totalEventCount: diagnostic?.totalEventCount ?? diagnostic?.total_event_count ?? null,
  }));
};

const normalizeNameKey = (name, index, prefix = 'event') => {
  if (name !== undefined && name !== null) {
    return name.toString().trim().toLowerCase();
  }
  return `${prefix}-${index}`;
};

const normalizePixelEvents = (response, fallbackPixel, fallbackPixelId) => {
  const pixelIdentifier =
    response?.pixelId ||
    fallbackPixel?.pixelId ||
    fallbackPixel?.dataset_id ||
    fallbackPixel?.id ||
    fallbackPixelId ||
    'pixel';

  const resolvedPixelName =
    response?.pixelName ||
    fallbackPixel?.name ||
    fallbackPixel?.pixelName ||
    fallbackPixelId ||
    'Pixel';

  const webEventsArray = Array.isArray(response?.webEvents)
    ? response.webEvents
    : Array.isArray(response?.events)
    ? response.events
    : [];
  const offlineEventsArray = Array.isArray(response?.offlineEvents) ? response.offlineEvents : [];
  const aggregateDiagnosticsMap = new Map();
  if (Array.isArray(response?.webDiagnostics)) {
    response.webDiagnostics.forEach((aggregateEntry, diagIndex) => {
      const aggregateNameRaw = aggregateEntry?.eventName ?? `Event ${diagIndex + 1}`;
      const aggregateName = aggregateNameRaw != null ? aggregateNameRaw.toString() : `Event ${diagIndex + 1}`;
      const key = normalizeNameKey(aggregateNameRaw, diagIndex, 'aggregate');
      const normalizedDiagnostics = normalizeDiagnosticsList(
        aggregateEntry?.diagnostics,
        `${pixelIdentifier}-${aggregateName || diagIndex}-agg`,
        'aggregate'
      );
      if (normalizedDiagnostics.length) {
        aggregateDiagnosticsMap.set(key, {
          name: aggregateName,
          diagnostics: normalizedDiagnostics,
          matched: false,
        });
      }
    });
  }

  const normalizedWebEvents = webEventsArray.map((event, index) => {
    const eventNameRaw = event?.eventName ?? event?.name;
    const eventName = eventNameRaw !== undefined && eventNameRaw !== null
      ? eventNameRaw.toString()
      : `Event ${index + 1}`;
    const emqScoreValue = safeNumber(
      event?.compositeScore ?? event?.composite_score ?? event?.emqScore ?? event?.emq_score
    );
    const coverageValue = safeNumber(
      event?.eventCoveragePercentage ?? event?.coveragePercentage ?? event?.coverage
    );
    const acrValue = safeNumber(event?.acrPercentage ?? event?.acr_percentage ?? event?.acr);
    const emqRatingRaw = event?.emqRating ?? event?.emq_rating ?? null;
    const qualityLevel = determineQualityLevel(emqRatingRaw, coverageValue, emqScoreValue);

    const matchKeys = Array.isArray(event?.identifiers)
      ? event.identifiers.map((identifier, keyIndex) => {
          const identifierName =
            identifier?.identifier || identifier?.field || identifier?.matchKey || `Field ${keyIndex + 1}`;
          const identifierCoverage = safeNumber(
            identifier?.coverage ?? identifier?.coveragePercentage ?? identifier?.eventCoveragePercentage
          );
          const potentialAcrValue = safeNumber(
            identifier?.potentialACRIncrease ?? identifier?.potential_acr_increase
          );
          const coverageLevel = determineCoverageLevel(identifierCoverage);

          return {
            field: identifierName,
            coverageValue: identifierCoverage,
            coverage: formatPercentageValue(identifierCoverage),
            coverageLevel,
            potentialAcrValue,
            potentialACR: potentialAcrValue === null ? 'N/A' : formatPercentageValue(potentialAcrValue),
          };
        })
      : [];

    const baseId = `${pixelIdentifier}-${eventName || index}`;
    const directDiagnostics = normalizeDiagnosticsList(event?.diagnostics, baseId, 'primary');
    const aggregateKey = normalizeNameKey(eventNameRaw, index, 'event');
    const aggregateEntry = aggregateDiagnosticsMap.get(aggregateKey);
    const aggregateDiagnostics = aggregateEntry?.diagnostics || [];
    if (aggregateEntry) {
      aggregateEntry.matched = true;
    }
    const diagnostics = [...directDiagnostics, ...aggregateDiagnostics];

    return {
      id: `${pixelIdentifier}-${eventName || index}`,
      name: eventName,
      emqScore: emqScoreValue === null ? '—' : emqScoreValue.toFixed(1),
      emqScoreValue,
      emqRating: formatRatingLabel(emqRatingRaw, qualityLevel),
      emqRatingLevel: qualityLevel,
      acr: formatPercentageValue(acrValue),
      acrValue,
      coverage: formatPercentageValue(coverageValue),
      coverageValue,
      diagnostics,
      matchKeys,
    };
  });

  const leftoverAggregateEvents = Array.from(aggregateDiagnosticsMap.values()).map((entry, index) => {
    const baseId = `${pixelIdentifier}-aggregate-${index}`;
    return {
      id: baseId,
      name: entry.name,
      emqScore: '—',
      emqScoreValue: null,
      emqRating: 'N/A',
      emqRatingLevel: 'na',
      acr: '—',
      acrValue: null,
      coverage: '—',
      coverageValue: null,
      diagnostics: entry.diagnostics,
      matchKeys: [],
      matched: entry.matched,
    };
  });

  return {
    pixelName: resolvedPixelName,
    webEvents: [
      ...normalizedWebEvents,
      ...leftoverAggregateEvents.filter((entry) => !entry.matched),
    ],
    offlineEvents: offlineEventsArray.map((event, index) => {
      const eventName = event?.eventName || event?.name || `Offline Event ${index + 1}`;
      const { formatted: emqScore, numeric: emqScoreValue } = formatScoreValue(
        event?.compositeScore ?? event?.composite_score ?? event?.emqScore ?? event?.emq_score
      );
      const { formatted: freshnessScore, numeric: freshnessValue } = formatScoreValue(
        event?.freshnessScore ?? event?.freshness_score ?? event?.freshness
      );
      const { formatted: frequencyScore, numeric: frequencyValue } = formatScoreValue(
        event?.frequencyScore ?? event?.frequency_score ?? event?.frequency
      );
      const { formatted: matchKeyScore, numeric: matchKeyValue } = formatScoreValue(
        event?.matchKeyScore ?? event?.match_key_score ?? event?.matchKeyCoverage ?? event?.match_key_coverage
      );

      return {
        id: `${pixelIdentifier}-offline-${eventName || index}`,
        name: eventName,
        emqScore,
        emqScoreValue,
        freshnessScore,
        freshnessValue,
        frequencyScore,
        frequencyValue,
        matchKeyScore,
        matchKeyValue,
      };
    }),
  };
};

const DrillDownModal = ({ isOpen, onClose, pixelData, orgId }) => {
  const [expandedEventId, setExpandedEventId] = useState(null);
  const [webEvents, setWebEvents] = useState([]);
  const [offlineEvents, setOfflineEvents] = useState([]);
  const [activeSource, setActiveSource] = useState('web');
  const [pixelName, setPixelName] = useState(() => pixelData?.name || pixelData?.pixelName || 'Pixel');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expandedDiagnosticsId, setExpandedDiagnosticsId] = useState(null);
  const [firstRowExpanded, setFirstRowExpanded] = useState(false);
  const [oemqScores, setOemqScores] = useState([]);
  const [oemqLoading, setOemqLoading] = useState(false);
  const [oemqError, setOemqError] = useState(null);

  useEffect(() => {
    setPixelName(pixelData?.name || pixelData?.pixelName || 'Pixel');
  }, [pixelData]);

  useEffect(() => {
    if (isOpen) {
      setActiveSource('web');
      setExpandedEventId(null);
      setExpandedDiagnosticsId(null);
      setFirstRowExpanded(false);
      setOemqScores([]);
      setOemqError(null);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const resolvedPixelId = (pixelData?.pixelId || pixelData?.dataset_id || pixelData?.id || pixelData?.pixel_id || '')
      .toString()
      .trim();

    if (!resolvedPixelId) {
      setWebEvents([]);
      setOfflineEvents([]);
      setError('Missing pixel identifier.');
      return undefined;
    }

    const parsedOrg =
      typeof orgId === 'number'
        ? orgId
        : typeof orgId === 'string'
        ? Number(orgId)
        : NaN;
    const resolvedOrgId = Number.isFinite(parsedOrg) && parsedOrg > 0 ? parsedOrg : null;

    if (!resolvedOrgId) {
      setWebEvents([]);
      setOfflineEvents([]);
      setError('Missing organization identifier.');
      return undefined;
    }

    let isMounted = true;
    const controller = new AbortController();

    const fetchEvents = async () => {
      try {
        setLoading(true);
        setError(null);
        setWebEvents([]);
        setOfflineEvents([]);
        setExpandedEventId(null);
        setExpandedDiagnosticsId(null);

        const endpoint = API_ENDPOINTS.PIXEL_EVENTS(resolvedOrgId, encodeURIComponent(resolvedPixelId));
        const response = await apiService.get(endpoint, { signal: controller.signal });

        if (!isMounted) {
          return;
        }

        const normalized = normalizePixelEvents(response, pixelData, resolvedPixelId);
        setPixelName(normalized.pixelName);
        setWebEvents(normalized.webEvents);
        setOfflineEvents(normalized.offlineEvents);
      } catch (err) {
        if (err?.name === 'AbortError' || err?.name === 'CanceledError' || err?.code === 'ERR_CANCELED') {
      return;
    }

        console.error('❌ Failed to load pixel events:', err);

        if (!isMounted) {
          return;
        }

        setError('Unable to load event data right now. Please try again later.');
        setWebEvents([]);
        setOfflineEvents([]);
        setExpandedEventId(null);
        setExpandedDiagnosticsId(null);
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchEvents();

    return () => {
      isMounted = false;
      controller.abort();
    };
  }, [isOpen, pixelData, orgId]);

  const summary = useMemo(() => {
    if (!webEvents.length) {
      return [
        { id: 'total-events', label: 'Total Events', value: '0', tooltip: 'Number of distinct web events fetched for this pixel.' },
        { id: 'avg-emq', label: 'Avg. EMQ Score', value: '—', tooltip: 'Average EMQ (event quality) score across all web events.' },
        { id: 'capi-events', label: 'CAPI Events', value: '0', tooltip: 'Count of web events with a numeric EMQ score.' },
        { id: 'high-quality', label: 'High Quality', value: '0', tooltip: 'Events with EMQ classification of High (≥8.0 by default).' },
        { id: 'medium-quality', label: 'Medium Quality', value: '0', tooltip: 'Events with EMQ classification of Medium (roughly 6.0-7.9).' },
        { id: 'low-quality', label: 'Low Quality', value: '0', tooltip: 'Events with EMQ classification of Low (<6.0) or missing data.' },
      ];
    }

    const totals = {
      high: 0,
      medium: 0,
      low: 0,
    };

    webEvents.forEach((event) => {
      if (event.emqRatingLevel === 'high') {
        totals.high += 1;
      } else if (event.emqRatingLevel === 'medium') {
        totals.medium += 1;
      } else if (event.emqRatingLevel === 'low') {
        totals.low += 1;
      }
    });

    const emqScores = webEvents
      .map((event) => event.emqScoreValue)
      .filter((value) => typeof value === 'number' && !Number.isNaN(value));

    const avgEmqScore =
      emqScores.length > 0
        ? (emqScores.reduce((sum, value) => sum + value, 0) / emqScores.length).toFixed(1)
        : '—';

    const capiEvents = webEvents
      .filter((event) => typeof event.emqScoreValue === 'number' && !Number.isNaN(event.emqScoreValue))
      .length;

    return [
      {
        id: 'total-events',
        label: 'Total Events',
        value: webEvents.length.toString(),
        tooltip: 'Number of distinct web events fetched for this pixel.',
      },
      {
        id: 'avg-emq',
        label: 'Avg. EMQ Score',
        value: emqScores.length ? avgEmqScore : '—',
        tooltip: 'Average EMQ (event quality) value across all web events with scores.',
      },
      {
        id: 'capi-events',
        label: 'CAPI Events',
        value: capiEvents.toString(),
        tooltip: 'Count of web events with a numeric EMQ score.',
      },
      {
        id: 'high-quality',
        label: 'High Quality',
        value: totals.high.toString(),
        tooltip: 'Events whose EMQ rating is High (typically ≥8.0).',
      },
      {
        id: 'medium-quality',
        label: 'Medium Quality',
        value: totals.medium.toString(),
        tooltip: 'Events whose EMQ rating is Medium (roughly 6.0-7.9).',
      },
      {
        id: 'low-quality',
        label: 'Low Quality',
        value: totals.low.toString(),
        tooltip: 'Events whose EMQ rating is Low (<6.0) or missing sufficient data.',
      },
    ];
  }, [webEvents]);

  const toggleEvent = (eventId) => {
    setExpandedEventId((prev) => (prev === eventId ? null : eventId));
    setExpandedDiagnosticsId((prev) => (prev === eventId ? null : prev));
  };

  const handleDiagnosticsToggle = useCallback((eventId) => {
    setExpandedDiagnosticsId((prev) => (prev === eventId ? null : eventId));
  }, []);

  const coverageBadgeClass = (level) => {
    switch (level) {
      case 'high':
        return 'coverage-badge high-coverage';
      case 'medium':
        return 'coverage-badge medium-coverage';
      case 'low':
        return 'coverage-badge low-coverage';
      default:
        return 'coverage-badge na-coverage';
    }
  };

  const ratingBadgeClass = (level) => {
    switch (level) {
      case 'high':
        return 'quality-score-indicator high-quality';
      case 'medium':
        return 'quality-score-indicator medium-quality';
      case 'low':
        return 'quality-score-indicator low-quality';
      default:
        return 'quality-score-indicator na-quality';
    }
  };

  const handleSourceChange = useCallback((source) => {
    setActiveSource(source);
    setExpandedEventId(null);
    setExpandedDiagnosticsId(null);
    setFirstRowExpanded(false);
    setOemqScores([]);
    setOemqError(null);
  }, []);

  const fetchOemqScores = useCallback(async () => {
    if (!isOpen || !pixelData || !orgId) {
      return;
    }

    const resolvedPixelId = (pixelData?.pixelId || pixelData?.dataset_id || pixelData?.id || pixelData?.pixel_id || '')
      .toString()
      .trim();

    if (!resolvedPixelId) {
      setOemqError('Missing pixel identifier.');
      return;
    }

    const parsedOrg =
      typeof orgId === 'number'
        ? orgId
        : typeof orgId === 'string'
        ? Number(orgId)
        : NaN;
    const resolvedOrgId = Number.isFinite(parsedOrg) && parsedOrg > 0 ? parsedOrg : null;

    if (!resolvedOrgId) {
      setOemqError('Missing organization identifier.');
      return;
    }

    let isMounted = true;
    const controller = new AbortController();

    try {
      setOemqLoading(true);
      setOemqError(null);

      const endpoint = API_ENDPOINTS.OEMQ_SCORES(resolvedOrgId, resolvedPixelId);
      const response = await apiService.get(endpoint, { signal: controller.signal });

      if (!isMounted) {
        return;
      }

      if (Array.isArray(response)) {
        setOemqScores(response);
      } else {
        setOemqScores([]);
      }
    } catch (err) {
      if (err?.name === 'AbortError' || err?.name === 'CanceledError' || err?.code === 'ERR_CANCELED') {
        return;
      }

      console.error('❌ Failed to load OEMQ scores:', err);

      if (!isMounted) {
        return;
      }

      setOemqError('Unable to load OEMQ score data. Please try again later.');
      setOemqScores([]);
    } finally {
      if (isMounted) {
        setOemqLoading(false);
      }
    }

    return () => {
      isMounted = false;
      controller.abort();
    };
  }, [isOpen, pixelData, orgId]);

  const handleFirstRowToggle = useCallback(() => {
    setFirstRowExpanded((prev) => !prev);
  }, []);

  // Fetch OEMQ scores when first row is expanded
  useEffect(() => {
    if (firstRowExpanded && offlineEvents.length > 0 && oemqScores.length === 0 && !oemqLoading) {
      fetchOemqScores();
    }
  }, [firstRowExpanded, offlineEvents.length, oemqScores.length, oemqLoading, fetchOemqScores]);

  const sourceLabel = useMemo(() => (activeSource === 'offline' ? 'Offline' : 'Web'), [activeSource]);
  const noWebData = useMemo(() => !loading && !error && webEvents.length === 0, [loading, error, webEvents.length]);
  const noOfflineData = useMemo(
    () => !loading && !error && offlineEvents.length === 0,
    [loading, error, offlineEvents.length]
  );

  if (!isOpen) return null;

  return (
    <div className="quality-popup-overlay" onClick={onClose}>
      <div className="quality-popup" onClick={(event) => event.stopPropagation()}>
        <header className="quality-popup-header">
          <div className="quality-popup-title">
            <i className="fas fa-chart-line" />
            <span>Quality Data ({sourceLabel}) - {pixelName}</span>
          </div>
          <button type="button" className="quality-popup-close" onClick={onClose} aria-label="Close quality data modal">
            <i className="fas fa-times" />
          </button>
        </header>

        <div className="quality-popup-content">
          <div className="quality-source-tabs-wrapper">
            <div className="quality-source-tabs" role="tablist" aria-label="Pixel data sources">
              <button
                type="button"
                className={`quality-source-tab ${activeSource === 'web' ? 'active' : ''}`}
                onClick={() => handleSourceChange('web')}
                role="tab"
                aria-selected={activeSource === 'web'}
              >
                Web Events
              </button>
              <button
                type="button"
                className={`quality-source-tab ${activeSource === 'offline' ? 'active' : ''}`}
                onClick={() => handleSourceChange('offline')}
                role="tab"
                aria-selected={activeSource === 'offline'}
              >
                Offline Events
              </button>
            </div>
            </div>

          {activeSource === 'web' ? (
            <>
              {!noWebData && !error && (
                <section className="quality-summary-cards">
                  {summary.map((item) => (
                    <article className="quality-summary-card" key={item.id} title={item.tooltip}>
                      <h3 className="quality-summary-value">{item.value}</h3>
                      <p className="quality-summary-label">{item.label}</p>
                    </article>
                  ))}
                </section>
              )}

              {noWebData && !loading ? (
                <div className="no-data-state">
                  <div className="no-data-icon">
                    <i className="fas fa-info" />
                  </div>
                  <h3 className="no-data-title">No Web Quality Data Available</h3>
                  <p className="no-data-message">
                    We couldn’t find any recent Conversions API (CAPI) events for this pixel.
                  </p>
                  <p className="no-data-message">
                    <strong>Tips:</strong>
                  </p>
                  <ul className="no-data-tips">
                    <li>Ensure Conversions API (CAPI) is correctly implemented for this pixel.</li>
                    <li>Verify that events are being sent to this pixel via CAPI.</li>
                    <li>Check your Meta Access Token permissions.</li>
                    <li>Allow some time for data to populate after CAPI implementation.</li>
                  </ul>
                </div>
              ) : (
                <section className="quality-events-table-container">
                  <table className="quality-events-table">
                    <thead>
                      <tr>
                        <th>Event Name</th>
                        <th>EMQ Score</th>
                        <th>EMQ Rating</th>
                        <th>ACR</th>
                        <th>Coverage</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loading && (
                        <tr>
                          <td className="quality-events-loading" colSpan={6}>
                            Loading web events...
                          </td>
                        </tr>
                      )}
                      {!loading && error && (
                        <tr>
                          <td className="quality-events-error" colSpan={6}>
                            {error}
                          </td>
                        </tr>
                      )}
                      {!loading &&
                        !error &&
                        webEvents.map((event) => {
                          const isEventExpanded = expandedEventId === event.id;
                          const isDiagnosticsExpanded = expandedDiagnosticsId === event.id;
                          const shouldShowDetails = isEventExpanded || isDiagnosticsExpanded;
                          const acrSeverity = getPercentageSeverity(event.acrValue);
                          const coverageSeverity = getPercentageSeverity(event.coverageValue);
                          return (
                            <React.Fragment key={event.id}>
                              <tr>
                                <td className="event-name-cell">
                <button
                                    type="button"
                                    className="event-name-toggle"
                                    onClick={() => toggleEvent(event.id)}
                                    aria-expanded={isEventExpanded}
                                    aria-controls={`event-details-${event.id}`}
                                  >
                                    <span>{event.name}</span>
                                    <i
                                      className={`fas ${
                                        isEventExpanded ? 'fa-chevron-up' : 'fa-chevron-down'
                                      } event-toggle-icon`}
                                    />
                </button>
                                </td>
                                <td className="emq-score-cell">
                                  <strong>{event.emqScore}</strong>
                                </td>
                                <td>
                                  <span className={ratingBadgeClass(event.emqRatingLevel)}>{event.emqRating}</span>
                                </td>
                                <td className="acr-cell">
                                  <div className={`metric-value ${acrSeverity}`}>{event.acr}</div>
                                  <div className="metric-bar">
                                    <div
                                      className={`metric-bar-fill ${acrSeverity}`}
                                      style={{ width: getMetricBarWidth(event.acrValue) }}
                                    />
                                  </div>
                                </td>
                                <td className="coverage-cell">
                                  <div className={`metric-value ${coverageSeverity}`}>{event.coverage}</div>
                                  <div className="metric-bar">
                                    <div
                                      className={`metric-bar-fill ${coverageSeverity}`}
                                      style={{ width: getMetricBarWidth(event.coverageValue) }}
                                    />
                                  </div>
                                </td>
                                <td>
                                  {event.diagnostics.length ? (
                <button
                                      type="button"
                                      className={`pixels-action-btn pixels-action-btn--primary diagnostics-button ${
                                        isDiagnosticsExpanded ? 'active' : ''
                                      }`}
                                      onClick={() => handleDiagnosticsToggle(event.id)}
                                      aria-expanded={isDiagnosticsExpanded}
                                    >
                                      <i className="fas fa-eye" />
                                      {isDiagnosticsExpanded ? 'Hide Diagnostics' : 'Diagnostics'}
                </button>
                                  ) : (
                                    <span className="no-diagnostics">—</span>
                                  )}
                                </td>
                              </tr>
                              {isEventExpanded && (
                                <tr className="event-details-row" id={`event-details-${event.id}`}>
                                  <td className="event-details-cell" colSpan={6}>
                                    <div className="event-details-content">
                                      {event.matchKeys.length > 0 ? (
                                        <div className="match-key-feedback">
                                          <h4>Match Key Feedback:</h4>
                                          <div className="match-key-header">
                                            <span className="match-key-column">Field</span>
                                            <span className="match-key-column">Coverage</span>
                                            <span className="match-key-column">Potential ACR Increase</span>
                                          </div>
                                          <ul className="match-key-list">
                                            {event.matchKeys.map((key, index) => (
                                              <li className="match-key-item" key={`${event.id}-${key.field}-${index}`}>
                                                <div className="match-key-info">
                                                  <strong>{key.field}</strong>
                                                  <span className={coverageBadgeClass(key.coverageLevel)}>
                                                    {key.coverage}
                                                  </span>
                                                  <span className="acr-increase-badge">{key.potentialACR}</span>
              </div>
                                              </li>
                                            ))}
                                          </ul>
                      </div>
                    ) : (
                                        <p className="no-match-keys-message">No identifier data available for this event.</p>
                    )}
                  </div>
                                  </td>
                                </tr>
                              )}

                              {event.diagnostics.length > 0 && isDiagnosticsExpanded && (
                                <tr className="event-details-row" id={`diagnostics-details-${event.id}`}>
                                  <td className="event-details-cell" colSpan={6}>
                                    <div className="event-details-content diagnostics-only-content">
                                      <div className="diagnostics-section">
                                        <h4>Diagnostics</h4>
                                        <ul className="diagnostics-list">
                                          {event.diagnostics.map((diagnostic) => {
                                            const formattedDiagnosticPercentage = formatPercentageValue(diagnostic.percentage);
                                            const showPercentage = formattedDiagnosticPercentage !== 'N/A';
                                            const showMetrics =
                                              diagnostic.affectedEventCount !== null || diagnostic.totalEventCount !== null;
                                            return (
                                              <li className="diagnostic-card" key={diagnostic.id}>
                                                <div className="diagnostic-header">
                                                  <span className="diagnostic-title">{diagnostic.name}</span>
                                                  {(showMetrics || showPercentage) && (
                                                    <div className="diagnostic-meta">
                                                      {showMetrics && (
                                                        <div className="diagnostic-metrics-inline">
                                                          {diagnostic.affectedEventCount !== null && (
                                                            <span>
                                                              Affected Events:{' '}
                                                              {diagnostic.affectedEventCount.toLocaleString()}
                                                            </span>
                                                          )}
                                                          {diagnostic.totalEventCount !== null && (
                                                            <span>
                                                              Total Events: {diagnostic.totalEventCount.toLocaleString()}
                                                            </span>
                                                          )}
                                                        </div>
                                                      )}
                                                      {showPercentage && (
                                                        <span className="diagnostic-percentage">
                                                          {formattedDiagnosticPercentage}
                                                        </span>
                                                      )}
                                                    </div>
                                                  )}
                                                </div>
                                                <p className="diagnostic-description">{diagnostic.description}</p>
                                                <div className="diagnostic-solution">
                                                  <strong>Suggested Fix:</strong>
                                                  <p>{diagnostic.solution}</p>
                    </div>
                                              </li>
                                            );
                                          })}
                                        </ul>
                                      </div>
                        </div>
                                  </td>
                                </tr>
                              )}
                            </React.Fragment>
                          );
                        })}
                    </tbody>
                  </table>
                </section>
              )}
            </>
          ) : (
            <>
              {loading ? (
                <div className="section-loading">
                  <LoadingSpinner size="md" message="Loading offline events..." />
                    </div>
              ) : error ? (
                <div className="quality-offline-error">{error}</div>
              ) : noOfflineData ? (
                <div className="no-data-state">
                  <div className="no-data-icon">
                    <i className="fas fa-database" />
                  </div>
                  <h3 className="no-data-title">No Offline Events Available</h3>
                  <p className="no-data-message">
                    We couldn’t find any offline conversions for this pixel yet. Offline events will appear here once received.
                  </p>
                </div>
              ) : (
                <section className="quality-events-table-container">
                  <table className="quality-events-table">
                    <thead>
                      <tr>
                        <th>Event Name</th>
                        <th>EMQ Score</th>
                        <th>Freshness Score</th>
                        <th>Frequency Score</th>
                        <th>Match Key Score</th>
                      </tr>
                    </thead>
                    <tbody className="quality-offline-events-table">
                      {offlineEvents.map((event, index) => {
                        const isFirstRow = index === 0;
                        return (
                          <React.Fragment key={event.id}>
                            <tr 
                              className={isFirstRow ? 'offline-event-row-clickable' : ''}
                              onClick={isFirstRow ? handleFirstRowToggle : undefined}
                              style={isFirstRow ? { cursor: 'pointer' } : {}}
                            >
                              <td className="event-name-cell">
                                {isFirstRow ? (
                                  <button
                                    type="button"
                                    className="event-name-toggle"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleFirstRowToggle();
                                    }}
                                    aria-expanded={firstRowExpanded}
                                  >
                                    <span>{event.name}</span>
                                    <i
                                      className={`fas ${
                                        firstRowExpanded ? 'fa-chevron-up' : 'fa-chevron-down'
                                      } event-toggle-icon`}
                                    />
                                  </button>
                                ) : (
                                  <span>{event.name}</span>
                                )}
                              </td>
                              <td className="emq-score-cell">
                                <strong>{event.emqScore}</strong>
                              </td>
                              <td>{event.freshnessScore}</td>
                              <td>{event.frequencyScore}</td>
                              <td>{event.matchKeyScore}</td>
                            </tr>
                            {isFirstRow && firstRowExpanded && (
                              <tr className="event-details-row" id={`oemq-details-${event.id}`}>
                                <td className="event-details-cell" colSpan={5}>
                                  <div className="event-details-content">
                                    {oemqLoading ? (
                                      <div className="section-loading">
                                        <LoadingSpinner size="md" message="Loading OEMQ scores..." />
                                      </div>
                                    ) : oemqError ? (
                                      <div className="quality-offline-error">{oemqError}</div>
                                    ) : (
                                      <OemqTrendGraph data={oemqScores} />
                                    )}
                                  </div>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </section>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default DrillDownModal;
